import {
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  IconButton,
  InputAdornment,
  Tooltip,
  Button,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import EventBusyIcon from '@material-ui/icons/EventBusy';
import * as React from 'react';
import { FormDialog } from 'components/FormDialog';
import {
  ExperienceService,
  ReservationService,
  CustomerService,
} from 'services';
import { Experience, ReservationStatus, Customer } from 'interfaces';
import { TableAvailabilityDialog } from '../TableAvailabilityDialog';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const pad = (n: number) => String(n).padStart(2, '0');
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const initialForm = () => {
  return {
    name: '',
    email: '',
    phone_number: '',
    party_size: 2,
    date: todayLocal(),
    time: '',
    custom_time: false,
    table_names: '',
    notes: '',
    admin_notes: '',
    experience_id: '' as '' | number,
    status: 'approved' as ReservationStatus,
    duration_minutes: 120,
    marketing_opt_in: true,
  };
};

export const NewReservationDialog: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [form, setForm] = React.useState(initialForm());
  const [experiences, setExperiences] = React.useState<Experience[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [availabilityOpen, setAvailabilityOpen] = React.useState(false);
  const [slots, setSlots] = React.useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = React.useState(false);

  // Customer autocomplete state (shared between name + phone fields)
  const [customerOptions, setCustomerOptions] = React.useState<Customer[]>([]);
  const [customerSearching, setCustomerSearching] = React.useState(false);
  const searchTimer = React.useRef<number | null>(null);

  const runCustomerSearch = React.useCallback((q: string) => {
    if (searchTimer.current) {
      window.clearTimeout(searchTimer.current);
    }
    if (!q || q.trim().length < 2) {
      setCustomerOptions([]);
      return;
    }
    setCustomerSearching(true);
    searchTimer.current = window.setTimeout(() => {
      CustomerService.search(q)
        .then((rows) => setCustomerOptions(rows || []))
        .catch(() => setCustomerOptions([]))
        .finally(() => setCustomerSearching(false));
    }, 220);
  }, []);

  const formatCustomerLabel = (c: Customer) =>
    [c.firstName, c.lastName].filter(Boolean).join(' ');

  const applyCustomer = (c: Customer | null) => {
    if (!c) return;
    setForm((p) => ({
      ...p,
      name: formatCustomerLabel(c) || p.name,
      email: c.emailAddress || p.email,
      phone_number: c.phoneNumber || p.phone_number,
    }));
  };

  React.useEffect(() => {
    if (!open) return;
    setForm(initialForm());
    setError(null);
    setSlots([]);
    ExperienceService.getAll()
      .then((list) => setExperiences(list || []))
      .catch(() => setExperiences([]));
  }, [open]);

  // Refresh slots whenever the date changes
  React.useEffect(() => {
    if (!open || !form.date) return;
    setSlotsLoading(true);
    ReservationService.getAvailability(form.date)
      .then((res) => setSlots(res?.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [open, form.date]);

  const setField = (
    field: keyof ReturnType<typeof initialForm>,
    value: any,
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.date) {
      setError('Please pick a date.');
      return;
    }
    if (!form.time || !/^\d{2}:\d{2}$/.test(form.time)) {
      setError('Please pick a valid time (HH:MM).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload: any = {
        name: form.name,
        email: form.email,
        phone_number: form.phone_number,
        party_size: form.party_size,
        reserved_at: `${form.date} ${form.time}:00`,
        // Send empty string instead of null. URLSearchParams stringifies
        // null to the literal "null" string which then lands in the DB.
        table_names: form.table_names || '',
        notes: form.notes || '',
        admin_notes: form.admin_notes || '',
        status: form.status,
        source: 'admin',
        duration_minutes: form.duration_minutes,
        marketing_opt_in: form.marketing_opt_in ? 1 : 0,
      };
      if (form.experience_id !== '') {
        payload.experience_id = form.experience_id;
      }
      await ReservationService.create(payload);
      onCreated();
    } catch (e) {
      setError((e as Error)?.message || 'Failed to create reservation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormDialog
      title="New Reservation"
      dialogProps={{
        open,
        onClose,
        fullWidth: true,
        maxWidth: 'md',
      }}
      submitText="Create"
      loading={submitting}
      onSubmit={handleSubmit}
    >
      {error && (
        <Typography color="error" variant="body2" style={{ marginBottom: 8 }}>
          {error}
        </Typography>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            options={customerOptions}
            loading={customerSearching}
            inputValue={form.name}
            onInputChange={(_: any, val: string) => {
              setField('name', val);
              runCustomerSearch(val);
            }}
            onChange={(_: any, val: Customer | string | null) => {
              if (val && typeof val !== 'string') applyCustomer(val);
            }}
            getOptionLabel={(opt) =>
              typeof opt === 'string' ? opt : formatCustomerLabel(opt)
            }
            filterOptions={(x) => x}
            renderOption={(opt) => (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>{formatCustomerLabel(opt)}</span>
                <span style={{ fontSize: '0.75rem', color: '#888' }}>
                  {opt.phoneNumber}
                  {opt.emailAddress ? ` · ${opt.emailAddress}` : ''}
                </span>
              </div>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Name" required fullWidth />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            options={customerOptions}
            loading={customerSearching}
            inputValue={form.phone_number}
            onInputChange={(_: any, val: string) => {
              setField('phone_number', val);
              runCustomerSearch(val);
            }}
            onChange={(_: any, val: Customer | string | null) => {
              if (val && typeof val !== 'string') applyCustomer(val);
            }}
            getOptionLabel={(opt) =>
              typeof opt === 'string' ? opt : opt.phoneNumber || ''
            }
            filterOptions={(x) => x}
            renderOption={(opt) => (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>{opt.phoneNumber}</span>
                <span style={{ fontSize: '0.75rem', color: '#888' }}>
                  {formatCustomerLabel(opt)}
                  {opt.emailAddress ? ` · ${opt.emailAddress}` : ''}
                </span>
              </div>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Phone" required fullWidth />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Email (optional)"
            type="email"
            value={form.email}
            onChange={(e) => setField('email', e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Party size"
            type="number"
            inputProps={{ min: 1, max: 50 }}
            value={form.party_size}
            onChange={(e) =>
              setField('party_size', parseInt(e.target.value, 10) || 1)
            }
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Duration (min)"
            type="number"
            inputProps={{ min: 30, step: 15 }}
            value={form.duration_minutes}
            onChange={(e) =>
              setField('duration_minutes', parseInt(e.target.value, 10) || 120)
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Date"
            type="date"
            value={form.date}
            onChange={(e) => {
              const v = e.target.value;
              setForm((p) => ({ ...p, date: v, time: '' }));
            }}
            InputLabelProps={{ shrink: true }}
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Table (comma-separated for multi-table)"
            value={form.table_names}
            onChange={(e) => setField('table_names', e.target.value)}
            placeholder="e.g. 22 or 16, 1, 13"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="See bookings already on this date">
                    <IconButton
                      size="small"
                      onClick={() => setAvailabilityOpen(true)}
                      disabled={!form.date}
                    >
                      <EventBusyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}
          >
            TIME
          </Typography>
          {!form.custom_time ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(86px, 1fr))',
                gap: 8,
                padding: 12,
                border: '1px solid rgba(0,0,0,0.15)',
                borderRadius: 6,
                minHeight: 64,
              }}
            >
              {slotsLoading && (
                <span
                  style={{
                    gridColumn: '1 / -1',
                    color: '#888',
                    textAlign: 'center',
                    fontSize: '0.9em',
                  }}
                >
                  Loading times…
                </span>
              )}
              {!slotsLoading && slots.length === 0 && (
                <span
                  style={{
                    gridColumn: '1 / -1',
                    color: '#888',
                    textAlign: 'center',
                    fontSize: '0.9em',
                  }}
                >
                  No standard slots for this date
                </span>
              )}
              {!slotsLoading &&
                slots.map((s) => {
                  const selected = form.time === s;
                  return (
                    <button
                      type="button"
                      key={s}
                      onClick={() =>
                        setForm((p) => ({ ...p, time: s }))
                      }
                      style={{
                        appearance: 'none',
                        background: selected ? '#2B2521' : '#F5F1EC',
                        color: selected ? '#F2E8E0' : '#2B2521',
                        border: '1.5px solid transparent',
                        borderColor: selected ? '#2B2521' : 'transparent',
                        borderRadius: 8,
                        padding: '10px 4px',
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: 'pointer',
                      }}
                    >
                      {s}
                    </button>
                  );
                })}
              <Button
                size="small"
                onClick={() =>
                  setForm((p) => ({ ...p, custom_time: true, time: '' }))
                }
                style={{
                  gridColumn: '1 / -1',
                  marginTop: slots.length > 0 ? 6 : 0,
                  fontSize: '0.78rem',
                }}
              >
                Other time…
              </Button>
            </div>
          ) : (
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={6} sm={4}>
                <TextField
                  type="time"
                  value={form.time}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((p) => ({ ...p, time: v }));
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 60 }}
                  fullWidth
                />
              </Grid>
              <Grid item>
                <Button
                  size="small"
                  onClick={() =>
                    setForm((p) => ({ ...p, custom_time: false, time: '' }))
                  }
                >
                  ← Back to standard slots
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={form.status}
              onChange={(e) =>
                setField('status', e.target.value as ReservationStatus)
              }
            >
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="requested">Requested</MenuItem>
              <MenuItem value="canceled">Canceled</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Experience (optional)</InputLabel>
            <Select
              value={form.experience_id}
              onChange={(e) => setField('experience_id', e.target.value as any)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {experiences.map((exp) => (
                <MenuItem key={exp.id} value={exp.id}>
                  {exp.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Customer notes"
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Admin notes"
            value={form.admin_notes}
            onChange={(e) => setField('admin_notes', e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.marketing_opt_in}
              onChange={(e) => {
                const v = e.target.checked;
                setForm((p) => ({ ...p, marketing_opt_in: v }));
              }}
            />
            <span>Customer opts in to occasional offers and event news from Peking Malta</span>
          </label>
        </Grid>
      </Grid>

      <TableAvailabilityDialog
        open={availabilityOpen}
        onClose={() => setAvailabilityOpen(false)}
        date={form.date}
        reservedAt={
          form.date && form.time ? `${form.date}T${form.time}` : ''
        }
        durationMinutes={form.duration_minutes}
      />
    </FormDialog>
  );
};
